import {
  IMAGE_GENERATION_ENABLED,
  SKETCH_GENERATION_ENABLED,
} from "../shared/features";
import { useEffect, useRef, useState } from "react";
import {
  AssetRecordType,
  createShapeId,
  type TLImageShape,
  type TLShapeId,
} from "@tldraw/tlschema";
import { Box } from "./canvas";
import type { WorkspaceModel } from "./useWorkspace";
import type { ImageJob } from "../shared/creative-generation";
import { GEMINI_IMAGE_MODEL } from "../shared/creative-generation";
import {
  creativeSourceKey,
  isCreativeImage,
  type CreativeDraft,
} from "./creative-tools";
import { api, uploadFile } from "./api";
import { sheetFor } from "./shapes";
import { sketchSelection } from "../shared/sketch-generation";

type Status = {
  configured: boolean;
  canGenerate: boolean;
  jobs: ImageJob[];
  dailyLimit: number;
  paused?: boolean;
};
export function useImageGeneration(
  model: WorkspaceModel,
  enabled: boolean,
  kind: "image" | "sketch" = "image",
) {
  const [status, setStatus] = useState<Status>({
    configured: false,
    canGenerate: false,
    jobs: [],
    dailyLimit: 10,
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const pending = useRef(false);
  const live = useRef(true);
  const route = `/api/rooms/${model.room.id}/creative-${kind === "sketch" ? "sketches" : "images"}`;
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    const refresh = () => {
      void api<Status>(route)
        .then((data) => {
          if (active) setStatus(data);
        })
        .catch(() => {
          if (active)
            setError(
              "Could not check image generation. Reopen the tools to reconnect.",
            );
        });
    };
    refresh();
    const timer = setInterval(refresh, 2500);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [enabled, route, model.room.platform]);

  const send = async (prepare: () => Promise<unknown>) => {
    if (
      !(kind === "sketch"
        ? SKETCH_GENERATION_ENABLED
        : IMAGE_GENERATION_ENABLED) ||
      pending.current ||
      !model.canEdit ||
      !status.canGenerate ||
      model.editor.getIsReadonly()
    )
      return;
    pending.current = true;
    setSending(true);
    setError("");
    try {
      const body = await prepare();
      if (!live.current || model.editor.getIsReadonly()) return;
      const job = await api<ImageJob>(route, body);
      if (live.current)
        setStatus((s) => ({
          ...s,
          jobs: [job, ...s.jobs.filter((j) => j.id !== job.id)],
        }));
    } catch (e) {
      // Never automatically resend a potentially accepted, chargeable request.
      if (live.current)
        setError(
          `${(e as Error).message} If the connection was interrupted, wait for the job status before trying again.`,
        );
    } finally {
      pending.current = false;
      if (live.current) setSending(false);
    }
  };

  const submit = async (shape: TLImageShape, draft: CreativeDraft) => {
    const selected = model.editor.getShape(shape.id);
    if (!selected || model.editor.isShapeOrAncestorLocked(selected)) {
      setError("Select an unlocked source image.");
      return;
    }
    const references = draft.references.map((id) =>
      model.editor.getShape(id as TLImageShape["id"]),
    );
    if (references.some((s) => !s || s.type !== "image")) {
      setError("A reference is missing. Select it again.");
      return;
    }
    const inputs = [selected, ...references];
    if (
      inputs.some(
        (s) =>
          !s ||
          !isCreativeImage(
            s,
            s.type === "image" && s.props.assetId
              ? model.editor.getAsset(s.props.assetId)
              : undefined,
            model.room.id,
          ),
      )
    ) {
      setError("An input is unavailable.");
      return;
    }
    await send(async () => ({
      id: crypto.randomUUID(),
      source: { id: shape.id, key: creativeSourceKey(shape) },
      references: references.map((s) => ({
        id: s!.id,
        key: creativeSourceKey(s as TLImageShape),
      })),
      prompt: draft.prompt,
      size: draft.size,
      area: draft.area,
    }));
  };

  const submitSketch = (
    snapshot: { ids: TLShapeId[]; key: string; blob: Blob },
    prompt: string,
    size: ImageJob["size"],
  ) =>
    send(async () => {
      const check = () => {
        const current = sketchSelection(snapshot.ids, (id) =>
          model.editor.store.get(id as TLShapeId),
        );
        if (current.key !== snapshot.key)
          throw new Error("The sketch changed. Refresh its preview.");
      };
      check();
      const hash = await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(snapshot.key),
      );
      const key = Array.from(new Uint8Array(hash), (b) =>
        b.toString(16).padStart(2, "0"),
      ).join("");
      const uploaded = await uploadFile(
        model.room.id,
        new File([snapshot.blob], "Sketch preview.png", { type: "image/png" }),
        undefined,
        model.room.platform === "cloudflare",
      );
      check();
      return {
        id: crypto.randomUUID(),
        source: { ids: snapshot.ids, key, file: uploaded.src.split("/").pop() },
        prompt,
        size,
        references: [],
        area: null,
      };
    });

  const add = (job: ImageJob) => {
    const { editor } = model;
    if (!job.output || !model.canEdit || editor.getIsReadonly()) return;
    const id = createShapeId(`ai-${job.id}`);
    if (editor.getShape(id)) {
      editor.select(id);
      editor.zoomToSelection({ animation: { duration: 200 } });
      return;
    }
    const sourceIds = job.sourceIds || [job.sourceId];
    const sources = sourceIds
      .map((id) => editor.getShape(id as TLShapeId))
      .filter((s) => !!s);
    const source = sources[0];
    if (
      !source ||
      sources.length !== sourceIds.length ||
      sources.some((s) => editor.isShapeOrAncestorLocked(s))
    ) {
      setError(
        "The source is missing or locked. Unlock it before adding the edited image.",
      );
      return;
    }
    const boxes = sources
      .map((s) => editor.getShapePageBounds(s))
      .filter((b): b is Box => !!b);
    const bounds = boxes.length ? Box.Common(boxes) : null;
    if (!bounds) return;
    const w = Math.min(640, bounds.w),
      h = (w * job.output.h) / job.output.w;
    let x = bounds.maxX + 32,
      y = bounds.minY;
    const all = editor
      .getCurrentPageShapes()
      .filter((s) => s.type !== "sheet")
      .map((s) => editor.getShapePageBounds(s))
      .filter(Boolean);
    for (let i = 0; i <= all.length; i++) {
      const hit = all.find(
        (b) =>
          b &&
          x < b.maxX + 16 &&
          x + w > b.minX - 16 &&
          y < b.maxY + 16 &&
          y + h > b.minY - 16,
      );
      if (!hit) break;
      x = hit.maxX + 32;
    }
    const assetId = AssetRecordType.createId(`ai-${job.id}`);
    const name = job.kind === "sketch" ? "Sketch image" : "Edited image";
    editor.markHistoryStoppingPoint(`Add ${name.toLowerCase()}`);
    editor.run(() => {
      if (!editor.getAsset(assetId))
        editor.createAssets([
          AssetRecordType.create({
            id: assetId,
            type: "image",
            props: {
              src: job.output!.src,
              name: `${name}.${job.output!.mimeType === "image/jpeg" ? "jpg" : job.output!.mimeType === "image/webp" ? "webp" : "png"}`,
              w: job.output!.w,
              h: job.output!.h,
              mimeType: job.output!.mimeType || "image/png",
              isAnimated: false,
            },
            meta: { imagePreviews: true },
          }),
        ]);
      editor.createShape<TLImageShape>({
        opacity: 1,
        id,
        type: "image",
        x,
        y,
        props: { assetId, w, h, altText: name },
        meta: {
          name,
          aiModel: GEMINI_IMAGE_MODEL,
          aiSource: job.sourceId,
          ...(job.sourceIds ? { aiSources: job.sourceIds } : {}),
          aiJob: job.id,
        },
      });
      const sheet = sheetFor(editor, source);
      if (
        sheet &&
        sources.every((s) => sheetFor(editor, s)?.id === sheet.id) &&
        !editor.isShapeOrAncestorLocked(sheet)
      )
        editor.reparentShapes([id], sheet.id);
      editor.select(id);
    });
    if (job.kind === "sketch") {
      const resultBounds = editor.getShapePageBounds(id);
      if (resultBounds)
        editor.zoomToBounds(Box.Common([bounds, resultBounds]), {
          targetZoom: editor.getZoomLevel(),
          inset: 160,
          animation: { duration: 200 },
        });
    } else editor.zoomToSelection({ animation: { duration: 200 } });
  };
  return { status, sending, error, submit, submitSketch, add };
}
